/**
 * Safety & Moderation Events
 *
 * Events related to crisis detection, resource display, user reports, and ToS acceptance.
 *
 * Privacy Note: These events NEVER include message content, only metadata.
 * GDPR Compliant: No PII stored, only aggregate metrics.
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Week 1)
 */

export type CrisisSeverity = 'low' | 'medium' | 'high' | 'critical';
export type DetectionSource = 'openai_moderation' | 'crisis_keywords' | 'user_flag';
export type ResourceType = '988_call' | 'crisis_text_line' | 'international_resources';
export type ConcernReason = 'self_harm' | 'crisis' | 'harmful_content' | 'other';
export type TosAcceptanceMethod = 'checkbox' | 'modal' | 'signup';
export type FalsePositiveCategory =
  | 'fictional'
  | 'academic'
  | 'creative'
  | 'historical'
  | 'actual_crisis'
  | 'other';
export type CrisisUrgencyLevel = 'standard' | 'urgent' | 'critical';

// ============================================================================
// EXPORTED ANALYTICS CONSTANTS (P2-8 - Centralized event names)
// ============================================================================

/**
 * Detection Feedback Analytics Event Names
 * Used by CrisisDetectionFeedbackModal for consistent event tracking
 */
export const SAFETY_FEEDBACK_EVENTS = {
  SUBMITTED: 'Crisis_Detection_Feedback_Submitted',
  FALSE_POSITIVE: 'Crisis_Detection_FalsePositive',
  ACCURATE: 'Crisis_Detection_Accurate',
  MODAL_OPENED: 'Crisis_Detection_FeedbackModalOpened',
  MODAL_SKIPPED: 'Crisis_Detection_FeedbackSkipped',
} as const;

/**
 * Practitioner Crisis Support Analytics Event Names
 * Used by CrisisPractitionerPrompt for consistent event tracking
 */
export const SAFETY_PRACTITIONER_EVENTS = {
  REQUEST_SUPPORT_TAPPED: 'CrisisPractitioner_RequestSupport_Tapped',
  REQUEST_SENT: 'CrisisPractitioner_Request_Sent',
  REQUEST_FAILED: 'CrisisPractitioner_Request_Failed',
} as const;

/**
 * Detection Feedback V2 Analytics Event Names (Bottom Sheet Flow)
 * Used by CrisisFeedbackBottomSheet for the streamlined 2-tap feedback flow
 *
 * Replaces the 4-stage modal wizard with a minimal bottom sheet.
 * Multi-Model Consensus (Gemini 3 Pro, GPT-5 Pro) - 2025-12-21
 */
export const SAFETY_FEEDBACK_V2_EVENTS = {
  // Bottom sheet lifecycle
  SHEET_OPENED: 'Crisis_FeedbackBottomSheet_Opened',
  SHEET_CLOSED: 'Crisis_FeedbackBottomSheet_Closed',
  // Inline trigger on CrisisBanner
  TRIGGER_CLICKED: 'Crisis_FeedbackTrigger_Clicked',
  // Category selection (step 1)
  CATEGORY_SELECTED: 'Crisis_FeedbackCategory_Selected',
  // Subtag selection (step 2 for false positives)
  SUBTAG_SELECTED: 'Crisis_FeedbackSubtag_Selected',
  // Submission outcomes
  SUBMITTED: 'Crisis_FeedbackV2_Submitted',
  ERROR: 'Crisis_FeedbackV2_Error',
} as const;

export type SafetyEventProperties = {
  // ============================================================================
  // CRISIS DETECTION EVENTS (Backend-triggered)
  // ============================================================================

  /**
   * Crisis indicators detected in analysis
   *
   * When: analyze-drama Edge Function detects crisis keywords
   * Why: Track detection accuracy, severity distribution, and response rates
   *
   * Privacy: No message content included, only detection metadata
   */
  'Safety_CrisisDetected': {
    severity: CrisisSeverity;
    detection_source: DetectionSource;
    confidence: number;
    show_resources: boolean;
  };

  /**
   * Crisis resources shown to user
   *
   * When: Banner auto-expands or user opens resources modal
   * Why: Track resource impression rate and trigger sources
   */
  'Safety_ResourcesShown': {
    triggered_by: 'crisis_detection' | 'user_initiated';
    severity: CrisisSeverity;
    screen: 'home' | 'results' | 'history';
  };

  // ============================================================================
  // FRONTEND UI EVENTS
  // ============================================================================

  /**
   * Crisis banner displayed on screen
   *
   * When: CrisisBanner component renders
   * Why: Track banner impression rate per screen
   */
  'Safety_BannerShown': {
    screen: 'home' | 'results' | 'history';
    crisis_detected: boolean;
    severity?: CrisisSeverity;
  };

  /**
   * User taps crisis banner
   *
   * When: User clicks "Need help? Get support" banner
   * Why: Track engagement rate with safety features
   */
  'Safety_BannerClicked': {
    screen: 'home' | 'results' | 'history';
    crisis_detected: boolean;
  };

  /**
   * User clicks a crisis resource
   *
   * When: User taps call, text, or international link
   * Why: PRIMARY SAFETY METRIC - track actual help-seeking behavior
   */
  'Safety_ResourceClicked': {
    resource_type: ResourceType;
    screen: 'home' | 'results' | 'history';
    triggered_by: 'crisis_detection' | 'user_initiated';
  };

  // ============================================================================
  // USER REPORT EVENTS ("I'm Concerned" Flag)
  // ============================================================================

  /**
   * User submits concern flag
   *
   * When: User completes "I'm Concerned" report form
   * Why: Track user-initiated safety reports for internal review
   */
  'Safety_ConcernFlagged': {
    reason: ConcernReason;
    has_analysis_id: boolean;
    has_context: boolean;
  };

  /**
   * User opens concern flag modal
   *
   * When: User taps "I'm Concerned" button
   * Why: Track engagement with reporting feature
   */
  'Safety_ConcernModalOpened': {
    screen: 'results' | 'modal';
    triggered_by: 'banner_button' | 'analysis_action';
  };

  // ============================================================================
  // TERMS OF SERVICE EVENTS
  // ============================================================================

  /**
   * User accepts Terms of Service
   *
   * When: User checks ToS checkbox and continues
   * Why: Track ToS acceptance rate and compliance
   */
  'Safety_TosAccepted': {
    version: string;
    method: TosAcceptanceMethod;
    is_update: boolean;
  };

  /**
   * User declines Terms of Service
   *
   * When: User clicks "Decline" on ToS modal
   * Why: Track decline rate and identify UX friction
   */
  'Safety_TosDeclined': {
    version: string;
    method: TosAcceptanceMethod;
  };

  /**
   * ToS modal displayed
   *
   * When: ToS modal renders (first use or version update)
   * Why: Track modal impression rate
   */
  'Safety_TosModalShown': {
    version: string;
    trigger: 'first_analysis' | 'version_update' | 'settings';
  };

  // ============================================================================
  // FEEDBACK V2 EVENTS (Bottom Sheet Flow - 2025-12-21)
  // ============================================================================

  /**
   * User taps "Was this banner accurate?" inline trigger
   *
   * When: CrisisBanner feedback trigger is clicked
   * Why: Track entry point engagement for new feedback flow
   */
  'Crisis_FeedbackTrigger_Clicked': {
    screen: 'home' | 'results' | 'history';
    severity: CrisisSeverity;
    moderation_event_id?: string;
  };

  /**
   * Feedback bottom sheet opened
   *
   * When: CrisisFeedbackBottomSheet becomes visible
   * Why: Track feedback flow initiation rate
   */
  'Crisis_FeedbackBottomSheet_Opened': {
    moderation_event_id?: string;
    severity: CrisisSeverity;
  };

  /**
   * Feedback bottom sheet closed
   *
   * When: User dismisses bottom sheet without completing
   * Why: Track abandonment rate and identify friction points
   */
  'Crisis_FeedbackBottomSheet_Closed': {
    step: 'category' | 'subtag';
    had_selection: boolean;
    moderation_event_id?: string;
  };

  /**
   * User selects feedback category (step 1)
   *
   * When: User taps "Accurate" or "False Positive" chip
   * Why: Track accurate vs false positive ratio
   */
  'Crisis_FeedbackCategory_Selected': {
    category: 'accurate' | 'false_positive';
    moderation_event_id?: string;
  };

  /**
   * User selects false positive subtag (step 2)
   *
   * When: User taps a subtag chip (roleplay, quote, sarcasm, etc.)
   * Why: Track false positive categories for detection improvement
   */
  'Crisis_FeedbackSubtag_Selected': {
    subtag: string;
    category_feedback: string;
    moderation_event_id?: string;
  };

  /**
   * Feedback V2 successfully submitted
   *
   * When: crisis-feedback-submit Edge Function returns success
   * Why: PRIMARY METRIC - track completion rate and accuracy data
   */
  'Crisis_FeedbackV2_Submitted': {
    was_accurate: boolean;
    category_feedback?: string;
    moderation_event_id?: string;
    feedback_id?: string;
  };

  /**
   * Feedback V2 submission error
   *
   * When: crisis-feedback-submit Edge Function returns error
   * Why: Track error rate for reliability monitoring
   */
  'Crisis_FeedbackV2_Error': {
    error: string;
    moderation_event_id?: string;
  };
};
